import { useCallback, useEffect, useState } from 'react';
import {
  getWebPushPublicKey,
  getWebPushStatus,
  subscribeWebPush,
  unsubscribeWebPush,
} from '../services/webPush';
import {
  WEB_PUSH_STATE,
  describeSubscribeError,
  deriveWebPushState,
  mapPublicKeyError,
  urlBase64ToUint8Array,
} from '../utils/webPushState';

const isSupported = () =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window
  && window.isSecureContext;

/** iOS требует установки на «Домой» до включения push (ТЗ 9.2). */
export const needsIosHomeScreen = () => {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const standalone = window.navigator.standalone === true
    || window.matchMedia?.('(display-mode: standalone)').matches;
  return iOS && !standalone;
};

/**
 * Web Push v0: capability detection, разрешение, регистрация service worker,
 * подписка. Регистрация SW ленивая — только по нажатию кнопки, а не при каждом
 * заходе на сайт.
 */
export const useWebPush = () => {
  const supported = isSupported();
  const [permission, setPermission] = useState(
    supported ? Notification.permission : 'default'
  );
  const [backend, setBackend] = useState('ready');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!supported) return undefined;
    let cancelled = false;
    getWebPushStatus()
      .then((status) => { if (!cancelled) setSubscribed(Boolean(status?.active)); })
      .catch((statusError) => { if (!cancelled) setBackend(mapPublicKeyError(statusError)); });
    return () => { cancelled = true; };
  }, [supported]);

  const enable = useCallback(() => {
    setBusy(true);
    setError('');

    // Разрешение спрашиваем только здесь, по явному действию пользователя.
    return Notification.requestPermission()
      .then((result) => {
        setPermission(result);
        if (result !== 'granted') return null;
        return getWebPushPublicKey().catch((keyError) => {
          setBackend(mapPublicKeyError(keyError));
          throw keyError;
        });
      })
      .then((config) => {
        if (!config?.public_key) return null;
        setBackend('ready');
        return navigator.serviceWorker.register('/sw.js')
          .then(() => navigator.serviceWorker.ready)
          .then((registration) => registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(config.public_key),
          }));
      })
      .then((subscription) => {
        if (!subscription) return null;
        const json = subscription.toJSON();
        return subscribeWebPush({
          endpoint: json.endpoint,
          expirationTime: json.expirationTime ?? null,
          keys: json.keys,
        });
      })
      .then((result) => {
        // Локальный granted без успешного POST каналом не считается (ТЗ 9.2).
        if (result) setSubscribed(true);
      })
      .catch((enableError) => {
        setSubscribed(false);
        setError(describeSubscribeError(enableError));
      })
      .finally(() => setBusy(false));
  }, []);

  const disable = useCallback(() => {
    setBusy(true);
    setError('');

    return navigator.serviceWorker.getRegistration()
      .then((registration) => registration?.pushManager.getSubscription())
      .then((subscription) => {
        if (!subscription) return null;
        const { endpoint } = subscription;
        return subscription.unsubscribe().then(() => unsubscribeWebPush(endpoint));
      })
      .then(() => {
        // status not_found тоже означает «не подписан»: операция идемпотентна.
        setSubscribed(false);
      })
      .catch((disableError) => {
        setError(describeSubscribeError(disableError));
      })
      .finally(() => setBusy(false));
  }, []);

  return {
    state: deriveWebPushState({ supported, permission, backend, subscribed }),
    WEB_PUSH_STATE,
    busy,
    error,
    enable,
    disable,
  };
};

export default useWebPush;
